/**
 * Membership Page for Olympic Fit Wellness
 * Clean premium design with Stripe subscription checkout
 */
import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Star, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipPage() {
  const [tiers, setTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [searchParams] = useSearchParams();
  const selectedTier = searchParams.get('tier');
  const autoCheckout = searchParams.get('checkout') === 'true';
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadTiers();
  }, []);

  // Auto-checkout after signup redirect
  // Check for both 'tier' (old) and 'plan' (new) params for backwards compatibility
  const planFromUrl = searchParams.get('plan');
  const tierToCheckout = selectedTier || planFromUrl;
  
  useEffect(() => {
    if (isAuthenticated && autoCheckout && tierToCheckout) {
      handleSubscribe(tierToCheckout);
    }
  }, [isAuthenticated, autoCheckout, tierToCheckout]);

  const loadTiers = async () => {
    try {
      const data = await api.getMembershipTiers();
      setTiers(data || []);
    } catch (error) {
      console.error('Error loading tiers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (tierName) => {
    // If not logged in, redirect to signup with the selected plan
    if (!isAuthenticated) {
      navigate(`/signup?plan=${tierName.toLowerCase()}&redirect=membership`);
      return;
    }

    setCheckoutLoading(tierName);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: tierName.toLowerCase(),
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        console.error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="bg-white" data-testid="membership-page">
      {/* Hero */}
      <section className="relative bg-neutral-900 pt-32 pb-20">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <span className="badge-accent mb-6 inline-flex">Membership Plans</span>
          <h1 className="text-display-2 font-heading text-white mb-6">
            Choose Your Path to Wellness
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            Flexible membership options designed to fit your lifestyle and goals.
          </p>
        </div>
      </section>

      {/* Pricing Toggle */}
      <section className="py-12 border-b border-neutral-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-neutral-900' : 'text-neutral-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-14 h-8 bg-neutral-200 rounded-full transition-colors"
              data-testid="billing-toggle"
            >
              <span 
                className={`absolute top-1 w-6 h-6 bg-accent-500 rounded-full transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-neutral-900' : 'text-neutral-400'}`}>
              Yearly <span className="text-accent-600">(Save 17%)</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          {isLoading ? (
            <div className="flex justify-center">
              <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {tiers.map((tier) => (
                <div 
                  key={tier.tier}
                  className={`relative rounded-xl p-8 ${
                    tier.highlight 
                      ? 'bg-white border-2 border-accent-500 shadow-lg' 
                      : 'bg-white border border-neutral-200'
                  }`}
                  data-testid={`membership-tier-${tier.tier}`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <h3 className="text-xl font-heading font-bold text-neutral-900 mb-2">
                    {tier.name}
                  </h3>
                  
                  <p className="text-sm text-neutral-500 mb-6">
                    {tier.description}
                  </p>
                  
                  <div className="flex items-baseline gap-1 mb-8">
                    <span className="text-4xl font-heading font-bold text-neutral-900">
                      ${billingCycle === 'monthly' ? tier.price_monthly : Math.round(tier.price_yearly / 12)}
                    </span>
                    <span className="text-neutral-400">/mo</span>
                  </div>
                  
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-accent-600 mb-6">
                      ${tier.price_yearly}/year (billed annually)
                    </p>
                  )}
                  
                  <ul className="space-y-4 mb-8">
                    {tier.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.highlight ? 'text-accent-600' : 'text-neutral-400'}`} />
                        <span className="text-neutral-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => handleSubscribe(tier.name)}
                    disabled={checkoutLoading === tier.name}
                    className={`block w-full text-center py-3 rounded-lg font-medium transition-colors disabled:opacity-70 ${
                      tier.highlight 
                        ? 'bg-neutral-900 text-white hover:bg-neutral-800' 
                        : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200'
                    }`}
                    data-testid={`select-${tier.tier}`}
                  >
                    {checkoutLoading === tier.name ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      'Subscribe Now'
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-neutral-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-neutral-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Can I cancel my membership anytime?",
                  a: "Yes, you can cancel your membership at any time. Monthly members can cancel with 30 days notice. Annual members can cancel and receive a prorated refund."
                },
                {
                  q: "Can I switch between plans?",
                  a: "Absolutely! You can upgrade or downgrade your membership at any time. Changes take effect at the start of your next billing cycle."
                },
                {
                  q: "Do you offer a free trial?",
                  a: "Yes! We offer a complimentary 3-day pass to all new members. Simply visit any of our locations to get started."
                },
                {
                  q: "Can I freeze my membership?",
                  a: "Wellness and Elite members can freeze their membership for up to 90 days per year at no additional cost."
                }
              ].map((faq, idx) => (
                <div key={idx} className="bg-white p-6 rounded-lg border border-neutral-200">
                  <h3 className="font-semibold text-neutral-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-neutral-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            Ready to Transform Your Wellness Journey?
          </h2>
          <p className="text-neutral-400 mb-8 max-w-xl mx-auto">
            Join thousands of members who have already started their transformation at Olympic Fit Wellness.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup" className="btn-accent px-8 inline-flex items-center gap-2">
              Start Free Trial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary text-white border-neutral-600 hover:border-white px-8">
              Schedule a Tour
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
